import { Button, ButtonTypes, Card } from "@repo/ui";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Pencil } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { MyViewController as Api, type MyViewWidget } from "../../../api/myViewController";
import { keys } from "../queryKeys";
import commonStyles from "../shared/style.module.scss";
import { WidgetCard } from "../widgets/widgetCard";
import { arrange, COLUMNS, GAP, normalize, place, ROW_HEIGHT } from "./layout";
import styles from "./style.module.scss";

export function BoardCanvas({
	widgets,
	boardId,
	editing,
	setEditing,
	configure,
	remove,
	refresh,
	live,
}: {
	widgets: MyViewWidget[];
	boardId: number;
	editing: boolean;
	setEditing: (value: boolean) => void;
	configure: (widget: MyViewWidget) => void;
	remove: (widget: MyViewWidget) => void;
	refresh: number;
	live: boolean;
}) {
	const client = useQueryClient();
	const [draft, setDraft] = useState(() => widgets.map(normalize));
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const [active, setActive] = useState<number>();
	const container = useRef<HTMLDivElement>(null);
	const [width, setWidth] = useState(1000);
	const interaction = useRef<{
		widget: MyViewWidget;
		startX: number;
		startY: number;
		resize: boolean;
		before: MyViewWidget[];
		pointerId: number;
		cellWidth: number;
	}>();
	useEffect(() => {
		if (!editing) setDraft(widgets.map(normalize));
	}, [widgets, editing]);
	useEffect(() => {
		if (!container.current) return;
		const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
		observer.observe(container.current);
		return () => observer.disconnect();
	}, []);
	useEffect(() => {
		if (!editing) return;
		const unload = (e: BeforeUnloadEvent) => {
			e.preventDefault();
			e.returnValue = "";
		};
		window.addEventListener("beforeunload", unload);
		return () => window.removeEventListener("beforeunload", unload);
	}, [editing]);
	const mobile = width < 640;
	const cell = width / COLUMNS;
	function start(e: React.PointerEvent, widget: MyViewWidget, resize: boolean) {
		if (e.button !== 0 || busy || interaction.current || (mobile && !editing)) return;
		e.preventDefault();
		e.stopPropagation();
		e.currentTarget.setPointerCapture(e.pointerId);
		interaction.current = {
			widget,
			startX: e.clientX,
			startY: e.clientY,
			resize,
			before: draft,
			pointerId: e.pointerId,
			cellWidth: cell,
		};
		setEditing(true);
		setActive(widget.id);
	}
	function move(e: React.PointerEvent) {
		const current = interaction.current;
		if (!current || current.pointerId !== e.pointerId) return;
		const dx = Math.round((e.clientX - current.startX) / current.cellWidth),
			dy = Math.round((e.clientY - current.startY) / ROW_HEIGHT);
		const next = current.resize
			? {
					...current.widget,
					width: Math.max(2, Math.min(12 - current.widget.xPos, current.widget.width + dx)),
					height: Math.max(2, Math.min(100, current.widget.height + dy)),
				}
			: {
					...current.widget,
					xPos: Math.max(0, Math.min(12 - current.widget.width, current.widget.xPos + dx)),
					yPos: Math.max(0, current.widget.yPos + dy),
				};
		setDraft(place(current.before, next));
	}
	function finish(e: React.PointerEvent, cancel = false) {
		if (!interaction.current || interaction.current.pointerId !== e.pointerId) return;
		if (cancel && interaction.current) setDraft(interaction.current.before);
		interaction.current = undefined;
		setActive(undefined);
	}
	function keyboard(e: React.KeyboardEvent, widget: MyViewWidget, resize: boolean) {
		if (busy || (mobile && !editing) || !["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key))
			return;
		e.preventDefault();
		setEditing(true);
		const dx = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
		const dy = e.key === "ArrowDown" ? 1 : e.key === "ArrowUp" ? -1 : 0;
		setDraft(
			place(
				draft,
				resize
					? {
							...widget,
							width: Math.max(2, Math.min(12 - widget.xPos, widget.width + dx)),
							height: Math.max(2, widget.height + dy),
						}
					: { ...widget, xPos: widget.xPos + dx, yPos: widget.yPos + dy },
			),
		);
	}
	async function save() {
		setBusy(true);
		setError("");
		try {
			await Api.saveLayout(
				boardId,
				draft.map((w) => ({ id: w.id, xPos: w.xPos, yPos: w.yPos, width: w.width, height: w.height })),
			);
			client.setQueryData(keys.widgets(boardId), draft);
			setEditing(false);
			void client.invalidateQueries({ queryKey: keys.widgets(boardId) });
		} catch {
			setError("Layout could not be saved. Your changes are still here; retry or cancel to reload.");
		} finally {
			setBusy(false);
		}
	}
	return (
		<>
			<div className={styles.layoutTools}>
				<span>
					{editing
						? "Drag a title bar to move. Drag the lower corner to resize. Save when you’re done."
						: `${widgets.length} ${widgets.length === 1 ? "widget" : "widgets"} · ${mobile ? "Choose Edit layout to move or resize widgets" : "Drag a title bar to move · Drag a corner to resize"}`}
				</span>
				<div>
					{editing ? (
						<>
							<Button disabled={busy || active !== undefined} onClick={() => setDraft(arrange(draft))}>
								Auto arrange
							</Button>
							<Button
								disabled={busy}
								onClick={() => {
									setEditing(false);
									setError("");
									void client.invalidateQueries({ queryKey: keys.widgets(boardId) });
								}}
							>
								Cancel
							</Button>
							<Button
								types={[ButtonTypes.Primary]}
								disabled={busy || active !== undefined}
								onClick={save}
							>
								<Check size={16} />
								{busy ? "Saving…" : "Save layout"}
							</Button>
						</>
					) : (
						<Button disabled={!widgets.length} onClick={() => setEditing(true)}>
							<Pencil size={15} />
							Edit layout
						</Button>
					)}
				</div>
			</div>
			{error && (
				<p className={commonStyles.error} role="alert">
					{error}
				</p>
			)}
			<div
				ref={container}
				data-mobile={mobile && !editing}
				className={`${styles.canvas} ${editing ? styles.editing : ""} ${mobile && !editing ? styles.mobile : ""}`}
				style={{
					minHeight:
						mobile && !editing
							? undefined
							: Math.max(360, ...draft.map((w) => (w.yPos + w.height) * ROW_HEIGHT)),
				}}
				onPointerMove={move}
				onPointerUp={(e) => finish(e)}
				onPointerCancel={(e) => finish(e, true)}
				onLostPointerCapture={(e) => finish(e)}
			>
				{draft.map((widget) => (
					<Card
						key={widget.id}
						data-active={active === widget.id}
						className={`${styles.widget} ${active === widget.id ? styles.active : ""}`}
						style={
							mobile && !editing
								? {}
								: {
										left: widget.xPos * cell,
										top: widget.yPos * ROW_HEIGHT,
										width: widget.width * cell - GAP,
										height: widget.height * ROW_HEIGHT - GAP,
									}
						}
					>
						<WidgetCard
							widget={widget}
							editing={editing}
							onConfigure={() => configure(widget)}
							onDelete={() => remove(widget)}
							refresh={refresh}
							live={live && !editing}
							onPointer={(e, resize) => start(e, widget, resize)}
							onKey={(e, resize) => keyboard(e, widget, resize)}
						/>
					</Card>
				))}
			</div>
		</>
	);
}
